<template>
  <Modal
    class="instrument-risk-modal"
    is-open
    size="md-2"
    title="Perfil de investimento"
    @close="handleClose"
  >
    <template #default>
      <div class="instrument-risk-modal__content">
        <div class="instrument-risk-modal__item" data-testid="risk-message">
          <BaseText>
            Esse é um ativo de risco
            <b>{{ instrumentRiskTolerance }}</b
            >, mas seu perfil de investimento atual é
            <b>{{ customerRiskTolerance }}</b
            >.
            <span>
              Você pode atualizar seu perfil agora ou aceitar o risco e
              negociar.
            </span>
          </BaseText>
        </div>
        <div class="instrument-risk-modal__checkbox">
          <Checkbox v-model="hasAcceptedRisk" :disabled="loading">
            <BaseText data-testid="instrument-checkbox">
              Estou ciente e quero negociar produtos fora do meu nível de risco.
            </BaseText>
          </Checkbox>
        </div>
      </div>
    </template>
    <template #buttons>
      <ButtonSecondary
        class="instrument-risk-modal__button"
        label="Atualizar perfil de investimento"
        :disabled="loading"
        size="large"
        @click="goToSuitability"
      />
      <ButtonPrimary
        class="instrument-risk-modal__button"
        :disabled="!hasAcceptedRisk"
        :loading="loading"
        label="Aceitar e negociar"
        size="large"
        @click="acceptAndInvest"
      />
    </template>
  </Modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';

import {
  Row,
  BaseText,
  Checkbox,
  Modal,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';

import { RiskProfile } from '@/modules/trade/services/instruments/types';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import { acceptOutsideRiskProfileTerm } from '@/modules/trade/services/instruments';
import {
  trackRedirectToSuitability,
  trackAcceptRiskTerms,
  trackInstrumentRiskModalLoad
} from '@/modules/trade/views/Instrument/trackers';
import { RiskTermsConfirmation, RiskTermsAnswers } from './types';
import { NotificationTypes } from '@/modules/common/services/http/notification';

@Component({
  components: {
    BaseText,
    Row,
    Checkbox,
    Modal,
    ButtonPrimary,
    ButtonSecondary
  }
})
export default class InstrumentRiskModal extends Vue {
  @Prop({ type: Object, required: true })
  readonly riskProfile!: RiskProfile;

  @Prop({ type: String, required: true })
  readonly symbol!: string;

  @Prop({ type: String, required: true })
  readonly instrumentType!: string;

  @Prop({ type: Function, required: true })
  readonly close!: () => void;

  @Prop({ type: Function, required: true })
  readonly confirm!: (riskTerm: RiskTermsConfirmation) => void;

  @Prop({ type: String, required: true })
  readonly orderType!: string;

  hasAcceptedRisk = false;
  loading = false;
  readonly NebraskaColors = NebraskaColors;

  created() {
    trackInstrumentRiskModalLoad(this.orderType);
  }

  get instrumentRiskTolerance() {
    return this.riskProfile.instrumentRiskTolerance?.formatted;
  }

  get customerRiskTolerance() {
    return this.riskProfile.customerRiskTolerance?.formatted;
  }

  handleAnswerRiskTerms(answer: RiskTermsAnswers) {
    this.confirm({ answer });
    this.close();
  }

  handleClose() {
    this.handleAnswerRiskTerms(RiskTermsAnswers.CLOSE_RISK_TERMS);
  }

  goToSuitability() {
    trackRedirectToSuitability(this.symbol);
    this.handleAnswerRiskTerms(RiskTermsAnswers.REDO_SUITABILITY);
  }

  async acceptAndInvest() {
    trackAcceptRiskTerms(this.symbol);
    this.loading = true;
    try {
      await acceptOutsideRiskProfileTerm(this.symbol);

      this.handleAnswerRiskTerms(RiskTermsAnswers.ACCEPT_RISK_TERMS);
    } catch (error) {
      this.$notify({
        group: 'wrn-trade',
        text: 'Não foi possível realizar essa operação, tente novamente mais tarde.',
        type: NotificationTypes.error
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>

<style lang="less" scoped>
.instrument-risk-modal {
  /deep/ .scroll-shadow {
    height: 100%;
  }

  /deep/ .modal-buttons {
    background: @background-secondary;
    border-top: @size-border-x400 solid @divider-primary;
  }

  &__button {
    margin: 0 @size-spacing-x150;
  }

  &__item {
    text-align: left;
    margin-bottom: @size-spacing-x300;
    p {
      text-align: left;
      margin-bottom: @size-spacing-x500;
    }
  }

  &__checkbox {
    margin-left: calc(@size-spacing-x300 * -1);
  }
}
</style>
